const GEO_URL = "https://trademarkfacility.com/GeoLocation/GetGeoLocation";
const LEAD_URL = "https://trademarkfacility.com/api/Lead/AddLead";

// XHR-based request to avoid third-party fetch monkey-patch issues (e.g. session recorders
// that clone responses and break the body stream).
function httpRequest(method, url, { body = null, headers = {} } = {}) {
  return new Promise((resolve) => {
    try {
      const xhr = new XMLHttpRequest();
      xhr.open(method, url, true);
      Object.keys(headers).forEach((k) => xhr.setRequestHeader(k, headers[k]));
      xhr.onload = () => {
        let parsed = null;
        try {
          parsed = JSON.parse(xhr.responseText);
        } catch {
          /* non-json */
        }
        resolve({
          ok: xhr.status >= 200 && xhr.status < 300,
          status: xhr.status,
          data: parsed,
          text: xhr.responseText,
        });
      };
      xhr.onerror = () =>
        resolve({
          ok: false,
          status: 0,
          data: null,
          text: "",
          networkError: true,
        });
      xhr.ontimeout = () =>
        resolve({
          ok: false,
          status: 0,
          data: null,
          text: "",
          networkError: true,
        });
      xhr.send(body);
    } catch (err) {
      resolve({
        ok: false,
        status: 0,
        data: null,
        text: "",
        networkError: true,
      });
    }
  });
}

const PAGE_URL_COOKIE = "tf_page_url";

function getCookie(name) {
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? decodeURIComponent(match[2]) : "";
}

function setCookie(name, value, days = 30) {
  const expires = new Date(
    Date.now() + days * 24 * 60 * 60 * 1000,
  ).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/`;
}

// Capture the page URL as soon as this module loads (i.e. as soon as the site
// is opened), so that the very first page the user lands on is stored,
// regardless of any later client-side navigation/redirects.
if (typeof window !== "undefined" && !getCookie(PAGE_URL_COOKIE)) {
  setCookie(PAGE_URL_COOKIE, window.location.href);
}

function getLeadPageUrl() {
  return getCookie(PAGE_URL_COOKIE) || window.location.href;
}

let geoCache = null;

export async function getGeoLocation() {
  if (geoCache) return geoCache;
  const res = await httpRequest("GET", GEO_URL, {
    headers: { accept: "text/plain" },
  });
  if (res.data?.isSuccess) {
    geoCache = res.data;
    return res.data;
  }
  return null;
}

export async function submitLead({
  name,
  email,
  countryDialCode,
  country,
  phoneNumber,
  description,
  packageTitle,
  packageAmount,
  tmSignUpType,
  geoData,
}) {
  if (!geoData) {
    geoData = await getGeoLocation();
  }

  const payload = {
    name,
    email,
    countryDialCode:
      parseInt(String(countryDialCode).replace("+", ""), 10) || 1,
    country: country || "US",
    phoneNumber: phoneNumber.replace(/[^0-9]/g, ""),
    ipAddress: geoData?.data?.ipAddress || "",
    description: description || "",
    pageUrl: getLeadPageUrl(),
    city: geoData?.data?.cityName || "",
    state: geoData?.data?.regionName || "",
    geoLocation: geoData ? JSON.stringify(geoData) : "",
    packageTitle: packageTitle || "Basic Package",
    packageAmount: String(packageAmount || "45").replace(/[^0-9.]/g, ""),
    tmSignUpType: tmSignUpType || 1,
  };

  const res = await httpRequest("POST", LEAD_URL, {
    body: JSON.stringify(payload),
    headers: { "Content-Type": "application/json" },
  });

  if (res.networkError) {
    return {
      success: false,
      error: "Network error. Please check your connection and try again.",
    };
  }

  const json = res.data || {};

  if (json.statusCode === 200 && json.isSuccess && json.data?.customerId) {
    return { success: true, customerId: json.data.customerId };
  }

  if (res.status === 401 || json.statusCode === 401) {
    return { success: false, error: json.message || "Unauthorized request" };
  }

  if (res.status === 400 || json.statusCode === 400) {
    if (json.errors) {
      const firstError = Object.values(json.errors)[0];
      return {
        success: false,
        error: Array.isArray(firstError) ? firstError[0] : firstError,
      };
    }
    return {
      success: false,
      error: json.errorMessage || json.data?.message || "Invalid request",
    };
  }

  return {
    success: false,
    error:
      json.errorMessage ||
      json.message ||
      "Something went wrong. Please try again.",
  };
}
